/**
 * Dashboard page.
 * Three-section layout:
 *   1. Owner — aircraft with hours, maintenance status, upcoming events (owner role only)
 *   2. Pilot — user's bookings & flights, with Log button for unlogged flights
 *   3. All   — fleet list with Book button per aircraft
 */

import { getUser, getRole, isOwner } from '../services/auth.js';
import { getFlights, getFlightsNeedingLog, getAircraft, getUpcomingBookings, getPilots } from '../services/api.js';
import { formatDateUK } from '../utils/format.js';
import { FLIGHT_STATUS } from '../utils/constants.js';
import { openFlightLogModal } from '../components/flight-log-modal.js';
import { openDetailModal } from '../components/detail-modal.js';
import { createFlight } from '../services/api.js';

/**
 * Format minutes as HH:MM string.
 */
function formatMinutes(mins) {
  if (mins == null) return '';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}:${String(m).padStart(2, '0')}`;
}

/**
 * Check if a flight row needs its log completed.
 */
export function needsFlightLog(flight) {
  return flight.status === FLIGHT_STATUS.APPROVED &&
    flight.flight_date <= new Date().toISOString().split('T')[0] &&
    flight.flight_time_mins == null;
}

// ── Section 1: Owner ──────────────────────────────────────────────────────

function buildOwnerAircraftCard(ac, upcomingEvents) {
  const today = new Date();

  // Maintenance status items
  let items = '';
  let worstSeverity = 0;

  function sev(cls) {
    if (cls === 'maint--overdue') return 2;
    if (cls === 'maint--amber') return 1;
    return 0;
  }

  function statusItem(label, valueStr, cls) {
    if (sev(cls) > worstSeverity) worstSeverity = sev(cls);
    return `<div class="maint-item ${cls}">
      <span class="maint-item__label">${label}</span>
      <span class="maint-item__value">${valueStr}</span>
    </div>`;
  }

  // 50hr check
  if (ac.hours_since_last_50hr != null) {
    const rem = 50 - Number(ac.hours_since_last_50hr);
    const cls = rem < 0 ? 'maint--overdue' : rem < 5 ? 'maint--amber' : 'maint--ok';
    items += statusItem('50hr Check', rem < 0 ? `${Math.abs(rem).toFixed(1)}h overdue` : `${rem.toFixed(1)}h remaining`, cls);
  }

  // Annual
  if (ac.last_annual_date) {
    const due = new Date(ac.last_annual_date);
    due.setFullYear(due.getFullYear() + 1);
    const daysUntil = Math.round((due - today) / 86400000);
    const cls = daysUntil < 0 ? 'maint--overdue' : daysUntil < 37 ? 'maint--amber' : 'maint--ok';
    items += statusItem('Annual', daysUntil < 0 ? `${Math.abs(daysUntil)}d overdue` : `Due ${due.toISOString().split('T')[0]}`, cls);
  }

  // Oil change
  if (ac.next_oil_change_hours != null && ac.total_hours != null) {
    const rem = Number(ac.next_oil_change_hours) - Number(ac.total_hours);
    const cls = rem < 0 ? 'maint--overdue' : rem < 5 ? 'maint--amber' : 'maint--ok';
    items += statusItem('Oil Change', rem < 0 ? `${Math.abs(rem).toFixed(1)}h overdue` : `${rem.toFixed(1)}h remaining`, cls);
  }

  const statusBadge = ac.status === 'UNSERVICEABLE'
    ? '<span class="badge badge--danger">U/S</span>'
    : '<span class="badge badge--success">OK</span>';

  const cardCls = worstSeverity === 2 ? 'maint-card--overdue'
    : worstSeverity === 1 ? 'maint-card--amber' : '';

  // Upcoming events for this aircraft
  let eventsHtml = '';
  if (upcomingEvents && upcomingEvents.length > 0) {
    eventsHtml = '<div class="owner-ac__events"><p class="owner-ac__events-label">Upcoming</p>' +
      upcomingEvents.slice(0, 3).map(e => `
        <div class="upcoming-flight">
          <span class="upcoming-flight__date">${formatDateUK(e.flight_date)}</span>
          <span class="upcoming-flight__aircraft">${(e.start_time || '').slice(0, 5)}</span>
          <span class="upcoming-flight__route">${e.pilots?.name || '—'}</span>
        </div>`).join('') + '</div>';
  }

  return `
    <div class="card maint-card ${cardCls} owner-ac-card">
      <div class="maint-card__header">
        <a href="#/aircraft/${ac.id}" class="maint-card__title-link"><h4 class="maint-card__title">${ac.registration}</h4></a>
        <span class="text-muted">${ac.type || ''}</span>
        ${statusBadge}
      </div>
      <div class="owner-ac__hours">
        <span>Airframe: <strong>${ac.airframe_hours ?? '—'}h</strong></span>
        <span>Engine: <strong>${ac.engine_hours ?? '—'}h</strong></span>
        <span>Prop: <strong>${ac.propeller_hours ?? '—'}h</strong></span>
      </div>
      ${items ? `<div class="maint-items">${items}</div>` : ''}
      ${eventsHtml}
    </div>
  `;
}

async function renderOwnerSection(container, aircraft) {
  const section = document.createElement('div');
  section.className = 'dashboard-section';
  section.innerHTML = '<h3 class="dashboard-section__title">Owner</h3><div class="owner-grid"></div>';
  container.appendChild(section);

  const grid = section.querySelector('.owner-grid');

  if (aircraft.length === 0) {
    grid.innerHTML = '<p class="text-muted">No active aircraft.</p>';
    return;
  }

  // Fetch upcoming bookings for each aircraft in parallel
  const bookingsPerAircraft = await Promise.all(
    aircraft.map(ac => getUpcomingBookings(ac.id).catch(() => []))
  );

  grid.innerHTML = aircraft.map((ac, i) =>
    buildOwnerAircraftCard(ac, bookingsPerAircraft[i])
  ).join('');
}

// ── Section 2: Pilot ──────────────────────────────────────────────────────

async function renderPilotSection(container, user) {
  const section = document.createElement('div');
  section.className = 'dashboard-section';
  section.innerHTML = '<h3 class="dashboard-section__title">My Flights</h3><div class="pilot-section-inner"><p class="text-muted">Loading…</p></div>';
  container.appendChild(section);

  const inner = section.querySelector('.pilot-section-inner');

  try {
    const [flights, flightsNeedingLog] = await Promise.all([
      getFlights({ pilot_id: user.id }),
      getFlightsNeedingLog(user.id),
    ]);

    const today = new Date().toISOString().split('T')[0];

    // Split into upcoming bookings and past flights
    const upcoming = flights
      .filter(f => (f.status === FLIGHT_STATUS.SUBMITTED || f.status === FLIGHT_STATUS.APPROVED) && f.flight_date >= today)
      .sort((a, b) => a.flight_date.localeCompare(b.flight_date));

    const recent = flights
      .filter(f => f.status === FLIGHT_STATUS.FLOWN || needsFlightLog(f))
      .sort((a, b) => b.flight_date.localeCompare(a.flight_date))
      .slice(0, 10);

    let html = '';

    // Flights needing log — highlight at top
    if (flightsNeedingLog.length > 0) {
      html += '<div class="pilot-action-strip">';
      html += flightsNeedingLog.map(f => `
        <div class="action-item action-item--warning">
          <span>${formatDateUK(f.flight_date)} — ${f.aircraft?.registration || '—'}</span>
          <button class="btn btn--sm btn--warning action-log-btn" data-id="${f.id}">Log</button>
        </div>`).join('');
      html += '</div>';
    }

    // Upcoming bookings
    if (upcoming.length > 0) {
      html += '<p class="pilot-subsection-label">Upcoming Bookings</p>';
      html += '<table class="table table--compact"><thead><tr><th>Date</th><th>Aircraft</th><th>Times</th><th>Route</th><th>Status</th></tr></thead><tbody>';
      html += upcoming.map(f => {
        const statusCls = f.status === FLIGHT_STATUS.APPROVED ? 'badge--success' : 'badge--info';
        return `<tr>
          <td>${formatDateUK(f.flight_date)}</td>
          <td>${f.aircraft?.registration || '—'}</td>
          <td>${(f.start_time || '').slice(0, 5)} – ${(f.end_time || '').slice(0, 5)}</td>
          <td>${f.departure || '—'} → ${f.destination || '—'}</td>
          <td><span class="badge ${statusCls}">${f.status}</span></td>
        </tr>`;
      }).join('');
      html += '</tbody></table>';
    } else {
      html += '<p class="text-muted">No upcoming bookings.</p>';
    }

    // Recent flights
    if (recent.length > 0) {
      html += '<p class="pilot-subsection-label">Recent Flights</p>';
      html += '<table class="table table--compact"><thead><tr><th>Date</th><th>Aircraft</th><th>Route</th><th>Flight Time</th><th>Status</th><th></th></tr></thead><tbody>';
      html += recent.map(f => {
        const isNeedsLog = needsFlightLog(f);
        const statusHtml = isNeedsLog
          ? '<span class="badge badge--warning">Needs Log</span>'
          : '<span class="badge badge--purple">FLOWN</span>';
        const logBtn = isNeedsLog
          ? `<button class="btn btn--sm btn--warning action-log-btn" data-id="${f.id}">Log</button>`
          : '';
        return `<tr class="${isNeedsLog ? 'row--needs-log' : ''}">
          <td>${formatDateUK(f.flight_date)}</td>
          <td>${f.aircraft?.registration || '—'}</td>
          <td>${f.departure || '—'} → ${f.destination || '—'}</td>
          <td>${formatMinutes(f.flight_time_mins)}</td>
          <td>${statusHtml}</td>
          <td>${logBtn}</td>
        </tr>`;
      }).join('');
      html += '</tbody></table>';
    }

    inner.innerHTML = html;

    // Attach Log button handlers
    inner.querySelectorAll('.action-log-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const allFlights = [...flightsNeedingLog, ...recent];
        const flight = allFlights.find(f => f.id === btn.dataset.id);
        if (flight) openFlightLogModal({ flight, onSaved: () => window.location.reload() });
      });
    });
  } catch (err) {
    inner.innerHTML = '<p class="text-danger">Failed to load flights.</p>';
    console.error('Pilot section error:', err);
  }
}

// ── Section 3: All — Fleet with Book ──────────────────────────────────────

function renderFleetSection(container, aircraft) {
  const section = document.createElement('div');
  section.className = 'dashboard-section';
  section.innerHTML = '<h3 class="dashboard-section__title">Fleet</h3>';
  container.appendChild(section);

  const grid = document.createElement('div');
  grid.className = 'fleet-grid';

  if (aircraft.length === 0) {
    grid.innerHTML = '<p class="text-muted">No active aircraft.</p>';
    section.appendChild(grid);
    return;
  }

  for (const ac of aircraft) {
    const card = document.createElement('div');
    card.className = 'aircraft-card fleet-card';

    const status = ac.status || 'unknown';
    const badgeClass = status === 'SERVICEABLE' || status === 'serviceable' ? 'badge--success'
      : status === 'UNSERVICEABLE' || status === 'unserviceable' ? 'badge--danger'
      : 'badge--muted';

    card.innerHTML = `
      <div class="fleet-card__top">
        <a href="#/aircraft/${ac.id}" class="fleet-card__reg">${ac.registration}</a>
        <span class="badge ${badgeClass}">${status === 'UNSERVICEABLE' || status === 'unserviceable' ? 'U/S' : 'OK'}</span>
      </div>
      <div class="fleet-card__type">${ac.type || ''}</div>
      <div class="fleet-card__location">${ac.location || ''}</div>
      <button class="btn btn--sm btn--primary fleet-card__book-btn" data-aircraft-id="${ac.id}" data-aircraft-reg="${ac.registration}" data-aircraft-type="${ac.type || ''}">Book</button>
    `;
    grid.appendChild(card);
  }

  section.appendChild(grid);

  // Book button handlers
  grid.querySelectorAll('.fleet-card__book-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      openBookingModal(btn.dataset.aircraftId, btn.dataset.aircraftReg, btn.dataset.aircraftType, aircraft);
    });
  });
}

function openBookingModal(aircraftId, aircraftReg, aircraftType, allAircraft) {
  const today = new Date().toISOString().split('T')[0];
  const aircraftOptions = allAircraft
    .filter(ac => !ac.archived && !(ac.meta?.retired) && !(ac.name || '').includes('[RETIRED]'))
    .map(ac => ({ value: ac.id, label: `${ac.registration} (${ac.type})` }));

  const fields = [
    { name: 'aircraft_id', label: 'Aircraft', type: 'select', options: aircraftOptions, required: true },
    { name: 'who_pays', label: 'Who Pays', type: 'select', options: [
      { value: 'CAPTAIN', label: 'Captain' },
      { value: 'P2', label: 'P2' },
    ], required: true },
    { name: 'flight_date', label: 'Start Date', type: 'date', required: true },
    { name: 'start_time', label: 'Start Time', type: 'time', required: true },
    { name: 'end_date', label: 'End Date', type: 'date', required: true },
    { name: 'end_time', label: 'End Time', type: 'time', required: true },
    { name: 'departure', label: 'From (Airfield)', type: 'text', placeholder: 'e.g. EGBJ' },
    { name: 'destination', label: 'To (Airfield)', type: 'text', placeholder: 'e.g. EGTK' },
    { name: 'expected_passengers', label: 'Expected Passengers', type: 'number', placeholder: '0' },
    { name: 'purpose', label: 'Purpose', type: 'text', placeholder: 'e.g. Cross-country nav exercise' },
  ];

  openDetailModal({
    title: `New Booking — ${aircraftReg}`,
    fields,
    values: {
      aircraft_id: aircraftId,
      flight_date: today,
      end_date: today,
    },
    onSave: async (formData) => {
      const user = getUser();
      const payload = {
        aircraft_id: formData.aircraft_id,
        pilot_id: user.id,
        flight_date: formData.flight_date,
        start_time: formData.start_time,
        end_date: formData.end_date || formData.flight_date,
        end_time: formData.end_time,
        departure: formData.departure || null,
        destination: formData.destination || null,
        expected_passengers: parseInt(formData.expected_passengers, 10) || 0,
        who_pays: formData.who_pays || 'CAPTAIN',
        purpose: formData.purpose || '',
        status: FLIGHT_STATUS.SUBMITTED,
      };
      await createFlight(payload);
      window.location.reload();
    },
  });
}

// ── Main entry ────────────────────────────────────────────────────────────

export async function renderDashboard() {
  const app = document.getElementById('app');
  const user = getUser();
  const role = getRole();

  app.innerHTML = `
    <div class="container">
      <h2 id="dashboard-heading">Dashboard</h2>
      <div id="dashboard-body"></div>
    </div>
  `;

  if (!user) return;

  // Set heading with user name
  const body = document.getElementById('dashboard-body');

  try {
    const [aircraft, pilots] = await Promise.all([getAircraft(), getPilots()]);
    const active = aircraft.filter(a => !a.archived && !a.meta?.retired && !(a.name || '').includes('[RETIRED]'));
    const currentPilot = pilots.find(p => p.id === user.id);
    const heading = document.getElementById('dashboard-heading');
    if (heading && currentPilot?.name) {
      heading.textContent = `Dashboard — ${currentPilot.name}`;
    }

    // Section 1: Owner (owner role only)
    if (role === 'owner') {
      await renderOwnerSection(body, active);
    }

    // Section 2: Pilot (all roles see their own flights)
    await renderPilotSection(body, user);

    // Section 3: Fleet with booking
    renderFleetSection(body, active);
  } catch (err) {
    body.innerHTML = `<p class="text-danger">Failed to load dashboard: ${err.message}</p>`;
    console.error('Dashboard error:', err);
  }
}

// ── Exported helpers for tests ────────────────────────────────────────────

export function getFlightGridColumns(role) {
  const columns = [
    { key: 'flight_date', label: 'Date' },
    {
      key: 'aircraft_registration', label: 'Aircraft', html: true,
      format: (_val, row) => {
        const reg = row.aircraft?.registration;
        const id = row.aircraft?.id || row.aircraft_id;
        if (!reg) return '';
        if (!id) return reg;
        return `<a href="#/aircraft/${id}" class="aircraft-link">${reg}</a>`;
      },
    },
    { key: 'departure', label: 'Departure' },
    { key: 'destination', label: 'Destination' },
    {
      key: 'status', label: 'Status', html: true,
      format: (val, row) => {
        if (needsFlightLog(row)) return `<span class="badge badge--warning badge--needs-log">Needs Log</span>`;
        const cls = { SUBMITTED: 'badge--info', APPROVED: 'badge--success', FLOWN: 'badge--purple', REJECTED: 'badge--danger', CANCELLED: 'badge--muted' }[val] || '';
        return `<span class="badge ${cls}">${val || ''}</span>`;
      },
    },
    { key: 'block_time_mins', label: 'Block Time', format: (val) => formatMinutes(val) },
    { key: 'flight_time_mins', label: 'Flight Time', format: (val) => formatMinutes(val) },
  ];
  if (role === 'admin' || role === 'owner') {
    columns.splice(1, 0, { key: 'pilot_name', label: 'Pilot', format: (_val, row) => row.pilots?.name || '' });
  }
  return columns;
}

export function filterFlightsByRole(flights, role, userId) {
  if (role === 'admin' || role === 'owner') return flights;
  return flights.filter((f) => f.pilot_id === userId);
}

export function renderLicencesCard(licences) {
  if (!licences || licences.length === 0) return '<p>No licences on record.</p>';
  const today = new Date().toISOString().split('T')[0];
  const rows = licences.map((lic) => {
    const expired = lic.expiry_date < today;
    const rowClass = expired ? ' class="licence-expired"' : '';
    const badge = expired ? ' <span class="badge badge--warning">Expired</span>' : '';
    return `<tr${rowClass}><td>${lic.licence_type || ''}</td><td>${lic.rating_category || ''}</td><td>${lic.expiry_date || ''}${badge}</td></tr>`;
  }).join('');
  return `<table class="table"><thead><tr><th>Licence Type</th><th>Rating Category</th><th>Expiry Date</th></tr></thead><tbody>${rows}</tbody></table>`;
}

export function calculateTotalHours(flights) {
  if (!flights || flights.length === 0) return 0;
  return flights.reduce((sum, f) => sum + (f.flight_time_mins || 0), 0);
}

export function calculate90DayHours(flights, referenceDate = new Date()) {
  if (!flights || flights.length === 0) return 0;
  const cutoff = new Date(referenceDate);
  cutoff.setDate(cutoff.getDate() - 90);
  const cutoffStr = cutoff.toISOString().split('T')[0];
  return flights.filter(f => f.flight_date >= cutoffStr).reduce((sum, f) => sum + (f.flight_time_mins || 0), 0);
}

export function getCurrencyWarnings(flights, thresholds, referenceDate = new Date()) {
  if (!thresholds || thresholds.length === 0) return [];
  const cutoff = new Date(referenceDate);
  cutoff.setDate(cutoff.getDate() - 90);
  const cutoffStr = cutoff.toISOString().split('T')[0];
  return thresholds
    .map(t => {
      const hours90Day = flights
        .filter(f => f.flight_date >= cutoffStr && (f.aircraft_type === t.aircraft_type || f.aircraft?.type === t.aircraft_type))
        .reduce((sum, f) => sum + (f.flight_time_mins || 0), 0);
      return { aircraftType: t.aircraft_type, hours90Day, minRequired: t.min_hours_90_days };
    })
    .filter(w => w.hours90Day < w.minRequired * 60);
}

export function renderCommonFleetSection() {
  // Deprecated — kept as no-op for backward compatibility with tests
}
